/**
 * A minimal promise wrapper over IndexedDB — no dependency, two object stores.
 *
 * The cache in front of /me/projects now (projectStore.server.ts), and still the ONLY copy of a
 * project built here before projects moved to the server until sign-in uploads it — so every
 * failure rejects. Swallowing a write here would lose someone's work silently, which is the one
 * outcome worth crashing over.
 */
const DB_NAME = 'saddle';
const VERSION = 1;

let cached: IDBDatabase | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (cached) return Promise.resolve(cached);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('projects')) {
        db.createObjectStore('projects', { keyPath: 'id' }).createIndex('owner_id', 'owner_id');
      }
      if (!db.objectStoreNames.contains('messages')) {
        db.createObjectStore('messages', { keyPath: 'id' }).createIndex('project_id', 'project_id');
      }
    };
    req.onsuccess = () => {
      cached = req.result;
      // Another tab upgrading the schema closes this connection out from under us; drop the
      // handle so the next call opens a fresh one instead of using a dead database.
      req.result.onversionchange = () => {
        req.result.close();
        cached = null;
      };
      resolve(req.result);
    };
    req.onerror = () => reject(req.error ?? new Error('Could not open local storage.'));
    // `blocked` fires instead of either of the above when another tab holds a connection at
    // an older version. Without this the promise never settles and every read and write
    // awaiting it hangs — a silent loss, not a crash. Latent at VERSION 1, live the moment
    // it is bumped.
    req.onblocked = () =>
      reject(new Error('Another tab has this app open at an older version. Close it and reload.'));
  });
}

/** Tests reopen the database between cases; production never calls this. */
export function closeDb(): void {
  cached?.close();
  cached = null;
}

function wrap<T>(req: IDBRequest<T>, tx: IDBTransaction): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Local storage request failed.'));
    tx.onabort = () => reject(tx.error ?? new Error('Local storage transaction aborted.'));
  });
}

export async function read<T>(store: string, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  const tx = db.transaction(store, 'readonly');
  return wrap(fn(tx.objectStore(store)), tx);
}

/**
 * A write completes when the TRANSACTION commits, not when the request succeeds — a
 * request can succeed and its transaction still abort on quota, leaving the caller
 * believing a draft was saved that was not.
 */
export async function write(store: string, fn: (s: IDBObjectStore) => void): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  fn(tx.objectStore(store));
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Local storage write failed.'));
    tx.onabort = () => reject(tx.error ?? new Error('Local storage write was aborted.'));
  });
}
